import { useMemoize } from "@vueuse/core";

// 公司数据类型定义
interface CompanyData {
  id: number;
  name: string;
  domain: string;
  logo?: string;
  description?: string;
  camera_enabled?: boolean; // 是否启用摄像头权限
  channel_source?: string; // 渠道来源
  practice_enabled?: boolean; // 是否开通陪练业务
  // 根据实际API响应添加其他字段
  [key: string]: unknown;
}

// 全局响应式状态
const companyData = ref<CompanyData | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);

const DEFAULT_DEV_COMPANY_DOMAIN = 'preview-coach.xinkauto.com'
const isDevelopmentEnv = process.env.NODE_ENV === 'development'

// 获取当前域名
const getCurrentDomain = (): string => {
  if (isDevelopmentEnv) {
    return DEFAULT_DEV_COMPANY_DOMAIN
  }

  if (import.meta.client) {
    return window.location.hostname
  }

  return DEFAULT_DEV_COMPANY_DOMAIN
};

// 根据域名获取公司数据（带缓存）
const getCompanyByDomain = useMemoize(
  async (domain: string): Promise<CompanyData> => {
    const { $myFetch } = useNuxtApp();
    
    return await ($myFetch as (url: string) => Promise<CompanyData>)(`/api/companies/domain/${domain}`);
  }
);

// 获取并存储公司数据
const fetchCompanyData = async (domain?: string): Promise<CompanyData | null> => {
  try {
    isLoading.value = true;
    error.value = null;
    
    const targetDomain = domain || getCurrentDomain();
    const data = await getCompanyByDomain(targetDomain);
    
    companyData.value = data;
    return data;
  } catch (err: unknown) {
    error.value = (err as Error)?.message || '获取公司信息失败';
    console.error('获取公司数据失败:', err);
    return null;
  } finally {
    isLoading.value = false;
  }
};

// 重置公司数据
const resetCompanyData = () => {
  companyData.value = null;
  error.value = null;
  isLoading.value = false;
};

// 更新公司数据
const updateCompanyData = (data: Partial<CompanyData>) => {
  if (companyData.value) {
    companyData.value = { ...companyData.value, ...data };
  }
};

export function useCompany() {
  return {
    // 响应式状态
    companyData: readonly(companyData),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // 方法
    fetchCompanyData,
    getCompanyByDomain,
    resetCompanyData,
    updateCompanyData,
    getCurrentDomain,
  };
} 
